// ============================================================================
// Supplier Performance Dashboard
// Displays key performance metrics for suppliers including on-time delivery,
// quality ratings, price competitiveness, and order accuracy
// Date: May 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Truck,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Clock,
  Star,
  BarChart3,
  Filter,
  Calendar,
  Download,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';

// ============================================================================
// TYPES
// ============================================================================

interface SupplierPerformanceMetrics {
  supplier_contact_id: number;
  supplier_name: string;
  period_start: string;
  period_end: string;
  
  // Delivery metrics
  total_orders: number;
  on_time_deliveries: number;
  on_time_rate: number;
  average_lead_time_days: number;
  late_deliveries: number;
  
  // Quality metrics
  total_items_received: number;
  defective_items: number;
  defect_rate: number;
  returns: number;
  return_rate: number;
  
  // Financial metrics
  total_spend: number;
  average_order_value: number;
  price_variance_vs_quote: number;
  invoices_disputed: number;
  
  // Accuracy metrics
  orders_complete: number;
  order_accuracy_rate: number;
  quantity_accuracy_rate: number;
  
  // Responsiveness
  average_quote_response_time_hours: number;
  rfqs_responded: number;
  rfqs_sent: number;
  rfq_response_rate: number;
  
  // Overall score
  overall_rating: number;
  rating_trend: 'up' | 'down' | 'stable';
}

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: number;
  color?: 'green' | 'amber' | 'red' | 'blue' | 'purple';
  isDark: boolean;
}

// ============================================================================
// METRIC CARD COMPONENT
// ============================================================================

function MetricCard({ label, value, icon: Icon, trend, trendValue, color = 'blue', isDark }: MetricCardProps) {
  const colorClasses = {
    green: isDark ? 'bg-green-900/20 text-green-300 border-green-800' : 'bg-green-50 text-green-700 border-green-200',
    amber: isDark ? 'bg-amber-900/20 text-amber-300 border-amber-800' : 'bg-amber-50 text-amber-700 border-amber-200',
    red: isDark ? 'bg-red-900/20 text-red-300 border-red-800' : 'bg-red-50 text-red-700 border-red-200',
    blue: isDark ? 'bg-blue-900/20 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-200',
    purple: isDark ? 'bg-purple-900/20 text-purple-300 border-purple-800' : 'bg-purple-50 text-purple-700 border-purple-200',
  };

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null;
  const trendColor = trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-gray-400';

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 opacity-70" />
        {TrendIcon && trendValue !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trendColor}`}>
            <TrendIcon className="w-3 h-3" />
            {Math.abs(trendValue).toFixed(1)}%
          </div>
        )}
      </div>
      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p className={`text-xs mt-1 opacity-80`}>{label}</p>
    </div>
  );
}

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

interface SupplierPerformanceDashboardProps {
  supplierContactId: number;
  supplierName: string;
  isDark: boolean;
}

type SupplierPeriod = '30d' | '90d' | '180d' | '365d';

function isSupplierPeriod(value: string): value is SupplierPeriod {
  return value === '30d' || value === '90d' || value === '180d' || value === '365d';
}

export default function SupplierPerformanceDashboard({
  supplierContactId,
  supplierName,
  isDark,
}: SupplierPerformanceDashboardProps) {
  const [metrics, setMetrics] = useState<SupplierPerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<SupplierPeriod>('90d');

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(
        `/api/v1/contacts/${supplierContactId}/performance-metrics?period=${period}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      const json = await response.json().catch(() => ({}));

      if (response.ok && json?.success) {
        setMetrics(json.data);
      } else {
        setError(json?.error || 'Failed to load performance metrics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load performance metrics');
    } finally {
      setLoading(false);
    }
  }, [supplierContactId, period]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
        <p className="text-sm">{error}</p>
        <button
          onClick={fetchMetrics}
          className="mt-2 text-sm underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className={`p-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No performance data available for this period</p>
      </div>
    );
  }

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'green';
    if (rating >= 3) return 'blue';
    if (rating >= 2) return 'amber';
    return 'red';
  };

  const getRateColor = (rate: number, inverse = false) => {
    if (inverse) {
      if (rate <= 2) return 'green';
      if (rate <= 5) return 'blue';
      if (rate <= 10) return 'amber';
      return 'red';
    }
    if (rate >= 95) return 'green';
    if (rate >= 85) return 'blue';
    if (rate >= 70) return 'amber';
    return 'red';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Supplier Performance
          </h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {supplierName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => {
              const nextValue = e.target.value;
              if (isSupplierPeriod(nextValue)) {
                setPeriod(nextValue);
              }
            }}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-gray-300'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="180d">Last 180 days</option>
            <option value="365d">Last year</option>
          </select>
          <button
            onClick={fetchMetrics}
            className={`p-2 rounded-lg border ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Overall Rating */}
      <div className={`p-4 rounded-lg border ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-full ${
              isDark ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <Star className={`w-6 h-6 ${
                metrics.overall_rating >= 4 ? 'text-yellow-500' :
                metrics.overall_rating >= 3 ? 'text-blue-500' :
                metrics.overall_rating >= 2 ? 'text-amber-500' : 'text-red-500'
              }`} />
            </div>
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Overall Rating
              </p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {metrics.overall_rating.toFixed(1)} / 5.0
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-1 text-sm ${
            metrics.rating_trend === 'up' ? 'text-green-500' :
            metrics.rating_trend === 'down' ? 'text-red-500' : 'text-gray-400'
          }`}>
            {metrics.rating_trend === 'up' && <TrendingUp className="w-4 h-4" />}
            {metrics.rating_trend === 'down' && <TrendingDown className="w-4 h-4" />}
            {metrics.rating_trend === 'stable' && <span className="text-xs">Stable</span>}
          </div>
        </div>
      </div>

      {/* Delivery Metrics */}
      <div>
        <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Delivery Performance
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label="On-Time Rate"
            value={`${metrics.on_time_rate.toFixed(1)}%`}
            icon={Truck}
            color={getRateColor(metrics.on_time_rate)}
            isDark={isDark}
          />
          <MetricCard
            label="Avg Lead Time"
            value={`${metrics.average_lead_time_days.toFixed(1)}d`}
            icon={Clock}
            color={getRateColor(metrics.average_lead_time_days, true)}
            isDark={isDark}
          />
          <MetricCard
            label="Total Orders"
            value={metrics.total_orders}
            icon={Package}
            color="blue"
            isDark={isDark}
          />
          <MetricCard
            label="Late Deliveries"
            value={metrics.late_deliveries}
            icon={AlertTriangle}
            color={metrics.late_deliveries > 0 ? 'amber' : 'green'}
            isDark={isDark}
          />
        </div>
      </div>

      {/* Quality Metrics */}
      <div>
        <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Quality & Returns
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label="Defect Rate"
            value={`${metrics.defect_rate.toFixed(2)}%`}
            icon={AlertTriangle}
            color={getRateColor(metrics.defect_rate, true)}
            isDark={isDark}
          />
          <MetricCard
            label="Return Rate"
            value={`${metrics.return_rate.toFixed(2)}%`}
            icon={Package}
            color={getRateColor(metrics.return_rate, true)}
            isDark={isDark}
          />
          <MetricCard
            label="Items Received"
            value={metrics.total_items_received}
            icon={Package}
            color="blue"
            isDark={isDark}
          />
          <MetricCard
            label="Defective Items"
            value={metrics.defective_items}
            icon={AlertTriangle}
            color={metrics.defective_items > 0 ? 'red' : 'green'}
            isDark={isDark}
          />
        </div>
      </div>

      {/* Financial Metrics */}
      <div>
        <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Financial Performance
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label="Total Spend"
            value={`R${metrics.total_spend.toLocaleString()}`}
            icon={DollarSign}
            color="green"
            isDark={isDark}
          />
          <MetricCard
            label="Avg Order Value"
            value={`R${metrics.average_order_value.toLocaleString()}`}
            icon={DollarSign}
            color="blue"
            isDark={isDark}
          />
          <MetricCard
            label="Price Variance"
            value={`${metrics.price_variance_vs_quote.toFixed(1)}%`}
            icon={TrendingUp}
            color={getRateColor(Math.abs(metrics.price_variance_vs_quote), true)}
            isDark={isDark}
          />
          <MetricCard
            label="Disputed Invoices"
            value={metrics.invoices_disputed}
            icon={AlertTriangle}
            color={metrics.invoices_disputed > 0 ? 'amber' : 'green'}
            isDark={isDark}
          />
        </div>
      </div>

      {/* Accuracy & Responsiveness */}
      <div>
        <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Accuracy & Responsiveness
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label="Order Accuracy"
            value={`${metrics.order_accuracy_rate.toFixed(1)}%`}
            icon={CheckCircle2}
            color={getRateColor(metrics.order_accuracy_rate)}
            isDark={isDark}
          />
          <MetricCard
            label="Quantity Accuracy"
            value={`${metrics.quantity_accuracy_rate.toFixed(1)}%`}
            icon={Package}
            color={getRateColor(metrics.quantity_accuracy_rate)}
            isDark={isDark}
          />
          <MetricCard
            label="RFQ Response Rate"
            value={`${metrics.rfq_response_rate.toFixed(1)}%`}
            icon={Clock}
            color={getRateColor(metrics.rfq_response_rate)}
            isDark={isDark}
          />
          <MetricCard
            label="Avg Response Time"
            value={`${metrics.average_quote_response_time_hours.toFixed(1)}h`}
            icon={Clock}
            color={getRateColor(metrics.average_quote_response_time_hours, true)}
            isDark={isDark}
          />
        </div>
      </div>
    </div>
  );
}
